import { useEffect, useRef, useState } from 'react'
import { useChat } from '@/hooks/useChat'
import { Bot, Send, RotateCcw, User, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { clsx } from 'clsx'

const QUICK_PROMPTS = [
  { label: 'Risk summary',     text: 'What is my current risk score and VaR?' },
  { label: 'Goals status',     text: 'How are my financial goals tracking?' },
  { label: 'Rebalance?',       text: 'Does my portfolio need rebalancing?' },
  { label: 'Market now',       text: 'What is the current market regime and opportunity score?' },
  { label: 'Stress level',     text: 'What does my behavioral profile look like right now?' },
  { label: 'Recommendations',  text: 'What are your top recommendations for me today?' },
]

/** Render markdown-style formatting without a full library. */
function InlineMarkdown({ text }: { text: string }) {
  // Split into paragraphs first, then process inline markdown
  const paragraphs = text.split(/\n\n+/)

  return (
    <div className="space-y-2">
      {paragraphs.map((para, pi) => {
        // Handle bullet lists
        const lines = para.split('\n')
        const isList = lines.every(l => l.trimStart().startsWith('- ') || l.trim() === '')
        if (isList && lines.some(l => l.trimStart().startsWith('- '))) {
          return (
            <ul key={pi} className="list-disc list-inside space-y-0.5">
              {lines.filter(l => l.trimStart().startsWith('- ')).map((line, li) => (
                <li key={li}><InlineSpan text={line.replace(/^\s*-\s*/, '')} /></li>
              ))}
            </ul>
          )
        }

        // Regular paragraph with line breaks preserved
        return (
          <p key={pi}>
            {lines.map((line, li) => (
              <span key={li}>
                {li > 0 && <br />}
                <InlineSpan text={line} />
              </span>
            ))}
          </p>
        )
      })}
    </div>
  )
}

/** Render inline formatting: **bold**, `code` */
function InlineSpan({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="font-semibold text-text-primary">{part.slice(2, -2)}</strong>
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return <code key={i} className="font-mono text-xs bg-surface-DEFAULT px-1 rounded">{part.slice(1, -1)}</code>
        }
        return <span key={i}>{part}</span>
      })}
    </>
  )
}

function AriaAvatar() {
  return (
    <div className="w-8 h-8 rounded-xl bg-purple/20 border border-purple/30 flex items-center justify-center shrink-0">
      <Bot size={15} className="text-purple-light" aria-hidden />
    </div>
  )
}

function UserAvatar() {
  return (
    <div className="w-8 h-8 rounded-xl bg-blue/20 border border-blue/30 flex items-center justify-center shrink-0">
      <User size={15} className="text-blue-light" aria-hidden />
    </div>
  )
}

function TypingIndicator() {
  return (
    <div className="flex items-end gap-2">
      <AriaAvatar />
      <div className="card px-4 py-3 text-sm text-text-muted flex items-center gap-2">
        <Loader2 size={13} className="animate-spin text-purple-light" aria-hidden />
        ARIA is thinking…
      </div>
    </div>
  )
}

export default function ChatPage() {
  const { messages, sending, error, send, clear } = useChat()
  const [input, setInput] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef  = useRef<HTMLInputElement>(null)

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, sending])

  const handleSend = () => {
    const text = input.trim()
    if (!text || sending) return
    setInput('')
    send(text)
  }

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple/20 border border-purple/30 flex items-center justify-center">
            <Bot size={20} className="text-purple-light" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text-primary">Chat with ARIA</h1>
            <p className="text-sm text-text-secondary">Adaptive Reasoning & Intelligence Agent</p>
          </div>
        </div>
        <button
          onClick={clear}
          className="btn-secondary gap-1.5 text-xs"
          aria-label="Clear conversation"
        >
          <RotateCcw size={13} aria-hidden /> Clear
        </button>
      </div>

      {/* Quick prompts */}
      <div className="flex flex-wrap gap-2 mb-4 shrink-0">
        {QUICK_PROMPTS.map(p => (
          <button
            key={p.label}
            onClick={() => send(p.text)}
            disabled={sending}
            className="px-3 py-1.5 text-xs rounded-full border border-border bg-surface-card
                       text-text-secondary hover:border-purple/40 hover:text-purple-light
                       hover:bg-purple/5 transition-colors disabled:opacity-50"
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Message thread */}
      <div
        className="flex-1 overflow-y-auto space-y-4 pr-1 pb-2"
        role="log"
        aria-label="Conversation with ARIA"
        aria-live="polite"
      >
        {messages.map(msg => (
          <div
            key={msg.id}
            className={clsx('flex items-end gap-2', msg.role === 'user' ? 'flex-row-reverse' : 'flex-row')}
          >
            {msg.role === 'aria' ? <AriaAvatar /> : <UserAvatar />}
            <div
              className={clsx(
                'max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-relaxed',
                msg.role === 'aria'
                  ? 'bg-surface-card border border-border text-text-secondary rounded-bl-sm'
                  : 'bg-blue/15 border border-blue/20 text-text-primary rounded-br-sm',
              )}
            >
              <InlineMarkdown text={msg.content} />
              <p className="text-[10px] text-text-muted mt-1.5 opacity-60">
                {format(msg.timestamp, 'HH:mm')}
              </p>
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {sending && <TypingIndicator />}

        {/* Error banner */}
        {error && (
          <div className="text-xs text-rose-light bg-rose/5 border border-rose/20 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div className="mt-4 shrink-0">
        <div className="flex items-center gap-2 card p-1.5">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask ARIA about your portfolio, risk, goals, or markets…"
            disabled={sending}
            className="flex-1 bg-transparent px-3 py-2 text-sm text-text-primary placeholder-text-muted
                       focus:outline-none disabled:opacity-50"
            aria-label="Message input"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className={clsx(
              'w-9 h-9 rounded-xl flex items-center justify-center transition-colors shrink-0',
              input.trim() && !sending
                ? 'bg-purple text-white hover:bg-purple/80'
                : 'bg-surface-hover text-text-muted cursor-not-allowed',
            )}
            aria-label="Send message"
          >
            {sending
              ? <Loader2 size={15} className="animate-spin" aria-hidden />
              : <Send size={15} aria-hidden />}
          </button>
        </div>
        <p className="text-[10px] text-text-muted mt-1.5 text-center">
          ARIA uses live portfolio data and LLM-powered analysis · Not financial advice
        </p>
      </div>
    </div>
  )
}
